import React, { useState } from "react";
import { Check, ChevronsUpDown, X, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";

// Searchable multi-select for trades / business types.
export function TradeMultiSelect({ options = [], selected = [], onChange, placeholder = "Search trades..." }) {
  const [open, setOpen] = useState(false);

  const toggle = (t) =>
    onChange(selected.includes(t) ? selected.filter((x) => x !== t) : [...selected, t]);

  return (
    <div data-testid="trade-multiselect">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid="trade-multiselect-trigger"
            role="combobox"
            aria-expanded={open}
            className="flex h-11 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 hover:border-primary/50 transition-colors"
          >
            <span className={selected.length ? "text-foreground" : "text-muted-foreground"}>
              {selected.length ? `${selected.length} trade${selected.length > 1 ? "s" : ""} selected` : "Select trades…"}
            </span>
            <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput data-testid="trade-multiselect-search" placeholder={placeholder} />
            <CommandList>
              <CommandEmpty>No trade found.</CommandEmpty>
              <CommandGroup>
                {options.map((t) => {
                  const on = selected.includes(t);
                  return (
                    <CommandItem
                      key={t}
                      value={t}
                      data-testid={`trade-option-${t}`}
                      onSelect={() => toggle(t)}
                      className="cursor-pointer"
                    >
                      <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded border ${on ? "bg-primary border-primary text-primary-foreground" : "border-input"}`}>
                        {on && <Check className="h-3 w-3" />}
                      </div>
                      {t}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2.5">
          {selected.map((t) => (
            <span
              key={t}
              data-testid={`trade-chip-${t}`}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20"
            >
              {t}
              <button
                type="button"
                data-testid={`trade-remove-${t}`}
                onClick={() => toggle(t)}
                className="hover:text-primary/70"
                aria-label={`Remove ${t}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
